import {
  cloneElement,
  isValidElement,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import {
  expandNoroshiReserve,
  hasInProgressNoroshiReserveSession,
  isNoroshiReserveEnabled,
  mountNoroshiReserve,
  openNoroshiReserve,
  reservationRedirectHref,
  rewriteNoroshiReserveCtaText,
  shouldAutoOpenNoroshiReserve,
  shouldMountNoroshiReserve,
  subscribeNoroshiReserveSession,
} from "./_noroshi-reserve";
import { pushNoroshiReserveCtaClick } from "@/lib/noroshi-reserve-bridge";

type NoroshiReserveCtaProps = {
  salonId: string;
  enabled?: boolean | number | null;
  menuId?: string | null;
  staffId?: string | null;
  mode?: string | null;
  className?: string;
  children: ReactNode;
};

function rewriteNoroshiReserveCtaCopy(node: ReactNode, inProgress: boolean): ReactNode {
  if (!inProgress) return node;
  if (typeof node === "string" || typeof node === "number") {
    return rewriteNoroshiReserveCtaText(String(node), true);
  }
  if (Array.isArray(node)) {
    return node.map((child, index) => {
      const rewritten = rewriteNoroshiReserveCtaCopy(child, true);
      if (isValidElement(rewritten) && rewritten.key == null) {
        return cloneElement(rewritten, { key: index });
      }
      return rewritten;
    });
  }
  if (isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    return cloneElement(node, undefined, rewriteNoroshiReserveCtaCopy(props.children, true));
  }
  return node;
}

export function NoroshiReserveCta({
  salonId,
  enabled,
  menuId,
  staffId,
  mode,
  className,
  children,
}: NoroshiReserveCtaProps) {
  const useNoroshi = isNoroshiReserveEnabled(enabled);
  const href = reservationRedirectHref({ salonId, menuId, staffId, mode });
  const [opening, setOpening] = useState(false);
  const [inProgress, setInProgress] = useState(hasInProgressNoroshiReserveSession);
  const resumeExisting = useNoroshi || inProgress;

  useEffect(() => subscribeNoroshiReserveSession(setInProgress), []);

  useEffect(() => {
    if (!useNoroshi && !inProgress) return;
    const autoOpen = shouldAutoOpenNoroshiReserve();
    if (!shouldMountNoroshiReserve({ enabled, autoOpen })) return;

    let active = true;
    const run = autoOpen ? openNoroshiReserve(salonId) : mountNoroshiReserve(salonId);
    void run.catch(() => {}).finally(() => {
      if (active) setOpening(false);
    });
    return () => {
      active = false;
    };
  }, [enabled, inProgress, salonId, useNoroshi]);

  if (!resumeExisting) {
    return (
      <a href={href} className={className}>
        {children}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={className}
      aria-busy={opening}
      onClick={() => {
        const resume = hasInProgressNoroshiReserveSession();
        pushNoroshiReserveCtaClick({ salonId, menuId, staffId, mode, resume });
        setOpening(true);
        const run = resume
          ? expandNoroshiReserve(salonId)
          : openNoroshiReserve(salonId);
        void run.catch(() => {}).finally(() => {
          setOpening(false);
          setInProgress(hasInProgressNoroshiReserveSession());
        });
      }}
    >
      {rewriteNoroshiReserveCtaCopy(children, inProgress)}
    </button>
  );
}
